// IM-UX-173/174 (IM-ADR-020): segundo fator do próprio titular — "Minha segurança" (códigos de recuperação e recadastro
// do aplicativo) e o cadastro inicial com o código entregue fora de banda (antes de entrar na administração).
import { useState, type FormEvent } from 'react';
import type { PlatformEnrollmentStartDto } from '@intelimove/contracts';
import { Modal } from '../components/Modal';
import { AsyncView } from '../components/States';
import { useToast } from '../components/Toast';
import { PageHead } from '../components/Widgets';
import { platformAdapter } from '../data/platform';
import { useResource } from '../hooks/useResource';
import { AuthenticatorSetup, CodeField, Kpis, RecoveryCodesReveal, STEP_UP_HINT, apiMessage, dt } from './shared';

export function PlatformMySecurity() {
  const toast = useToast();
  const { state, reload } = useResource((s) => platformAdapter.security(s), []);
  const [dialog, setDialog] = useState<'codes' | 'rebind' | null>(null);
  const close = (msg?: string) => { setDialog(null); if (msg) toast(msg); reload(); };
  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Administração da plataforma" title="Minha segurança" subtitle="Seu segundo fator e códigos de recuperação. Ninguém do suporte redefine o seu acesso." />
      <AsyncView state={state} onRetry={reload} empty={null}>
        {(s) => (
          <>
            {s.sessionMethod === 'RECOVERY' && <p className="notice-inline pa-notice" role="status"><b>Você entrou com um código de recuperação.</b> Operações sensíveis exigem o aplicativo: recadastre o aplicativo autenticador abaixo.</p>}
            <div className="pa-grid">
              <article className="card"><div className="card-head"><div><h2>Aplicativo autenticador</h2><div className="card-sub">{s.factor ? `Cadastrado em ${dt(s.factor.createdAt)}` : 'Não cadastrado'}</div></div>
                <button type="button" className="secondary" onClick={() => setDialog('rebind')}>Trocar aplicativo</button></div>
                <dl className="kv"><dt>Último uso</dt><dd>{dt(s.factor?.lastUsedAt)}</dd><dt>Origem</dt><dd>{s.factor?.enrolledVia === 'AUTOCADASTRO' ? 'Cadastrado por você' : 'Cadastrado pelo operador'}</dd></dl>
              </article>
              <article className="card"><div className="card-head"><div><h2>Códigos de recuperação</h2><div className="card-sub">{s.recovery.generatedAt ? `Gerados em ${dt(s.recovery.generatedAt)}` : 'Ainda não gerados'}</div></div>
                <button type="button" className={s.recovery.remaining ? 'secondary' : 'primary'} onClick={() => setDialog('codes')}>{s.recovery.remaining ? 'Gerar novos' : 'Gerar códigos'}</button></div>
                <Kpis items={[['Disponíveis', s.recovery.remaining]]} />
                {s.recovery.remaining < 3 && <p className="cell-sub wide">{s.recovery.remaining ? 'Poucos códigos restantes.' : 'Sem códigos: se perder o aparelho, só outro administrador recupera o seu acesso.'}</p>}
              </article>
            </div>
          </>
        )}
      </AsyncView>
      {dialog === 'codes' && <RegenerateCodesDialog onClose={close} />}
      {dialog === 'rebind' && <RebindDialog onClose={close} />}
    </section>
  );
}

function RegenerateCodesDialog({ onClose }: { onClose: (msg?: string) => void }) {
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { setError('Informe os 6 dígitos do aplicativo autenticador.'); return; }
    setBusy(true); setError(null);
    try { setCodes((await platformAdapter.regenerateRecovery(code)).recoveryCodes); } catch (err) { setError(apiMessage(err, 'Não foi possível gerar agora.')); setCode(''); setBusy(false); }
  };
  return (
    <Modal title="Códigos de recuperação" onClose={() => onClose(codes ? 'Códigos de recuperação gerados.' : undefined)}>
      {codes ? <RecoveryCodesReveal codes={codes} onDone={() => onClose('Códigos de recuperação gerados.')} /> : (
        <form className="form pa-form" onSubmit={submit} noValidate>
          <p>Os códigos atuais deixam de valer e 10 novos aparecem uma única vez.</p>
          {error && <div className="form-error" role="alert">{error}</div>}
          <CodeField id="pa-regen-code" value={code} onChange={setCode} hint={STEP_UP_HINT} />
          <div className="form-actions"><button type="button" className="secondary" onClick={() => onClose()}>Cancelar</button><button className="primary" type="submit" disabled={busy}>{busy ? 'Gerando…' : 'Gerar códigos'}</button></div>
        </form>
      )}
    </Modal>
  );
}

function RebindDialog({ onClose }: { onClose: (msg?: string) => void }) {
  const [password, setPassword] = useState('');
  const [start, setStart] = useState<PlatformEnrollmentStartDto | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const begin = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try { setStart(await platformAdapter.rebind(password)); setPassword(''); } catch (err) { setError(apiMessage(err, 'Não foi possível iniciar agora.')); }
    setBusy(false);
  };
  return (
    <Modal title="Trocar aplicativo autenticador" onClose={() => onClose(codes ? 'Aplicativo autenticador trocado.' : undefined)} wide={!!start && !codes}>
      {codes ? <RecoveryCodesReveal codes={codes} onDone={() => onClose('Aplicativo autenticador trocado.')} />
        : start ? <AuthenticatorSetup start={start} onCancel={() => onClose()} onConfirm={async (c) => setCodes((await platformAdapter.confirmEnrollment(start.enrollmentId, c)).recoveryCodes)} />
        : (
          <form className="form pa-form" onSubmit={begin} noValidate>
            <p>Confirme sua senha. O aplicativo atual continua valendo até você confirmar o novo; ao confirmar, suas outras sessões administrativas são encerradas e novos códigos de recuperação são gerados.</p>
            {error && <div className="form-error" role="alert">{error}</div>}
            <div className="field"><label htmlFor="pa-rebind-pass">Senha</label><input id="pa-rebind-pass" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
            <div className="form-actions"><button type="button" className="secondary" onClick={() => onClose()}>Cancelar</button><button className="primary" type="submit" disabled={busy || !password}>{busy ? 'Conferindo…' : 'Continuar'}</button></div>
          </form>
        )}
    </Modal>
  );
}

/** IM-UX-174: cadastro inicial do aplicativo com o código entregue por outro administrador (antes de entrar). */
export function EnrollWithTicket({ onDone }: { onDone: () => void }) {
  const [ticket, setTicket] = useState('');
  const [start, setStart] = useState<PlatformEnrollmentStartDto | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const begin = async (e: FormEvent) => {
    e.preventDefault();
    if (ticket.replace(/[^A-Za-z2-7]/g, '').length < 16) { setError('Informe o código de cadastro completo.'); return; }
    setBusy(true); setError(null);
    try { setStart(await platformAdapter.startEnrollment(ticket.trim())); setTicket(''); } catch (err) { setError(apiMessage(err, 'Não foi possível validar o código agora.')); }
    setBusy(false);
  };
  if (codes) return <RecoveryCodesReveal codes={codes} onDone={onDone} doneLabel="Continuar para a entrada" />;
  if (start) return <AuthenticatorSetup start={start} onConfirm={async (c) => setCodes((await platformAdapter.confirmEnrollment(start.enrollmentId, c)).recoveryCodes)} />;
  return (
    <form className="form" onSubmit={begin} noValidate>
      <p className="card-sub">Cadastre o aplicativo autenticador com o código de cadastro que um administrador da plataforma entregou a você.</p>
      {error && <div className="form-error" role="alert">{error}</div>}
      <div className="field"><label htmlFor="pa-ticket">Código de cadastro</label><input id="pa-ticket" value={ticket} onChange={(e) => setTicket(e.target.value.toUpperCase())} autoComplete="off" spellCheck={false} maxLength={64} placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX" /></div>
      <button className="primary" type="submit" disabled={busy}>{busy ? 'Validando…' : 'Cadastrar aplicativo'}</button>
    </form>
  );
}
